import { createAsyncThunk } from "@reduxjs/toolkit";
import { ACTION_DOCTOR_APPOINTMENTS } from "../../action-types/actions";
import { APP_PATH } from "../../api/backendPath";
import { toast } from '../../components/toast';
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

// ✅ Async thunk to fetch doctor appointments
export const fetchDoctorAppointmentsReducer = createAsyncThunk(
  ACTION_DOCTOR_APPOINTMENTS.fetch_doctor_appointments,
  async (
    { date, start_date, end_date, search = "", status, page = 1, limit = 10 } = {},
    { rejectWithValue }
  ) => {
    const axiosPrivate = await useAxiosPrivate();
    try {
      // Build query params object, only including defined and non-empty values
      const queryParamsObj = {};
      
      // Date handling: backend uses 'date' OR ('start_date' + 'end_date'), not both
      // If 'date' is provided, it takes precedence and start_date/end_date are ignored
      if (date) {
        // Format date to YYYY-MM-DD if it's a Date object or string
        let formattedDate = null;
        if (typeof date === 'string') {
          formattedDate = date.trim();
        } else if (date instanceof Date) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}`;
        } else {
          formattedDate = String(date);
        }
        if (formattedDate) {
          queryParamsObj.date = formattedDate;
        }
      } else {
        // Only include start_date/end_date if date is not provided
        if (start_date) {
          const formattedStartDate = typeof start_date === 'string' 
            ? start_date.trim() 
            : String(start_date);
          if (formattedStartDate) {
            queryParamsObj.start_date = formattedStartDate;
          }
        }
        
        if (end_date) {
          const formattedEndDate = typeof end_date === 'string' 
            ? end_date.trim() 
            : String(end_date);
          if (formattedEndDate) {
            queryParamsObj.end_date = formattedEndDate;
          }
        }
      }
      
      // Search parameter
      if (search && typeof search === 'string' && search.trim()) {
        queryParamsObj.search = search.trim();
      }
      
      // Status parameter
      if (status && typeof status === 'string' && status.trim()) {
        queryParamsObj.status = status.trim();
      }
      
      // Pagination parameters (always include with defaults)
      queryParamsObj.page = String(page || 1);
      queryParamsObj.limit = String(limit || 10);
      
      const queryParams = new URLSearchParams(queryParamsObj).toString();
      const fullUrl = `${process.env.REACT_APP_API_URL}${APP_PATH.getDoctorAppointments}${queryParams ? `?${queryParams}` : ''}`;
      const response = await axiosPrivate.get(fullUrl);

      if (response?.data?.status === true || response?.status === 200) {
        return response.data.data;
      } else {
        const msg = response?.data?.message || "Failed to fetch orders.";
        toast.error(msg);
        return rejectWithValue(msg);
      }
    } catch (error) {
      console.error("Error fetching doctor appointments:", error);
      const errMsg =
        error?.response?.data?.message ||
        "Something went wrong while fetching orders.";
      toast.error(errMsg);
      return rejectWithValue(errMsg);
    }
  }
);
